import {
  EmailAddress_WithIsEmailRegisteredAlready_DebouncedField,
  GenericForm,
  GenericFormLogic,
  emailAddress_withIsEmailRegisteredAlready_formLogic,
  nameFormLogic,
  mobileFormLogic,
  addressLineFormLogic,
  GenericTextFieldValidationOptions,
  postcodeFormLogic,
  countryFormLogic,
  AppMinimumRequirementsFor_StateFormLogic,
  stateFormLogic,
  addressAutocompleteFormLogic,
  AppMinimumRequirementsFor_AddressAutocomplete,
} from "../../../Common";
import {
  AppMinimumRequirementsFor_ShippingAutofill,
  shippingAutofillFormLogic,
} from "./autofill-form-logic";
import { isBillingAndShippingTheSameFormLogic } from "./is-billing-and-shipping-the-same-form-logic";

import {
  AppMinimumRequirementsFor_SavedAddress,
  savedAddressFormLogic,
} from "./saved-address-form-logic";

export interface ShippingForm extends GenericForm {
  emailAddress?: string;
  emailAddressError?: string;
  emailAddressDebouncedValidation?: EmailAddress_WithIsEmailRegisteredAlready_DebouncedField;

  shipping_isSavedAddress?: boolean;
  shipping_selectedSavedAddressId?: any;
  shipping_isManualAddress?: boolean;
  shipping_autocompleteAddress?: string;
  shipping_autocompleteAddress_selectedFromAutocomplete?: boolean;
  shipping_manualAddressLine1?: string;
  shipping_manualAddressLine2?: string;
  shipping_manualAddressSuburb?: string;
  shipping_manualAddressPostcode?: string;
  shipping_manualAddressState?: string;
  shipping_manualAddressCountry?: string;
  shipping_manualAddressFromParsed?: boolean;

  shipping_firstName?: string;
  shipping_lastName?: string;
  shipping_mobile?: string;

  isBillingAndShippingTheSame?: boolean;

  billing_isSavedAddress?: boolean;
  billing_selectedSavedAddressId?: any;
  billing_isManualAddress?: boolean;
  billing_autocompleteAddress?: string;
  billing_autocompleteAddress_selectedFromAutocomplete?: boolean;
  billing_manualAddressLine1?: string;
  billing_manualAddressLine2?: string;
  billing_manualAddressSuburb?: string;
  billing_manualAddressPostcode?: string;
  billing_manualAddressState?: string;
  billing_manualAddressCountry?: string;
  billing_manualAddressFromParsed?: boolean;

  billing_firstName?: string;
  billing_lastName?: string;
  billing_mobile?: string;

  authoriseDeliveryToBeLeftWithoutSignature?: boolean;
}

export const initialShippingForm = (): ShippingForm => ({
  isBillingAndShippingTheSame: true,
});

export interface InitialStateMinimumRequirements_ShippingForm {}

export type ShippingFormApp = AppMinimumRequirementsFor_StateFormLogic &
  AppMinimumRequirementsFor_AddressAutocomplete &
  AppMinimumRequirementsFor_SavedAddress &
  AppMinimumRequirementsFor_ShippingAutofill & {
    initialState: InitialStateMinimumRequirements_ShippingForm;
  };

const guardField_forBilling: GenericTextFieldValidationOptions<
  ShippingForm,
  ShippingFormApp
>["guard"] = (context) => {
  if (context.form.isBillingAndShippingTheSame) {
    return false;
  }
  return true;
};

const guardManualAddress: (
  addressIslandKey: string
) => GenericTextFieldValidationOptions<
  ShippingForm,
  ShippingFormApp
>["guard"] = (addressIslandKey: string) => (context) => {
  return (
    !context.form[`${addressIslandKey}_isSavedAddress`] &&
    context.form[`${addressIslandKey}_isManualAddress`]
  );
};
const guardManualAddress_forShipping = guardManualAddress("shipping");
const guardManualAddress_forBilling: GenericTextFieldValidationOptions<
  ShippingForm,
  ShippingFormApp
>["guard"] = (context) => {
  if (context.form.isBillingAndShippingTheSame) {
    return false;
  }
  return (
    guardManualAddress("billing") as Required<
      GenericTextFieldValidationOptions<ShippingForm, ShippingFormApp>
    >["guard"]
  )(context);
};

const guardAutocompleteAddress: (
  addressIslandKey: string
) => GenericTextFieldValidationOptions<
  ShippingForm,
  ShippingFormApp
>["guard"] = (addressIslandKey: string) => (context) => {
  return (
    !context.form[`${addressIslandKey}_isSavedAddress`] &&
    !context.form[`${addressIslandKey}_isManualAddress`]
  );
};
const guardAutocompleteAddress_forShipping =
  guardAutocompleteAddress("shipping");
const guardAutocompleteAddress_forBilling: GenericTextFieldValidationOptions<
  ShippingForm,
  ShippingFormApp
>["guard"] = (context) => {
  if (context.form.isBillingAndShippingTheSame) {
    return false;
  }
  return (
    guardAutocompleteAddress("billing") as Required<
      GenericTextFieldValidationOptions<ShippingForm, ShippingFormApp>
    >["guard"]
  )(context);
};

const guardSavedAddress: (
  addressIslandKey: string
) => GenericTextFieldValidationOptions<
  ShippingForm,
  ShippingFormApp
>["guard"] = (addressIslandKey: string) => (context) => {
  return context.form[`${addressIslandKey}_isSavedAddress`];
};
const guardSavedAddress_forShipping = guardSavedAddress("shipping");
const guardSavedAddress_forBilling: GenericTextFieldValidationOptions<
  ShippingForm,
  ShippingFormApp
>["guard"] = (context) => {
  if (context.form.isBillingAndShippingTheSame) {
    return false;
  }
  return (
    guardSavedAddress("billing") as Required<
      GenericTextFieldValidationOptions<ShippingForm, ShippingFormApp>
    >["guard"]
  )(context);
};

const guardOnlyWhenNotASavedAddress: (
  addressIslandKey: string
) => GenericTextFieldValidationOptions<
  ShippingForm,
  ShippingFormApp
>["guard"] = (addressIslandKey: string) => (context) => {
  return !context.form[`${addressIslandKey}_isSavedAddress`];
};
const guardOnlyWhenNotASavedAddress_forShipping =
  guardOnlyWhenNotASavedAddress("shipping");
const guardOnlyWhenNotASavedAddress_forBilling: GenericTextFieldValidationOptions<
  ShippingForm,
  ShippingFormApp
>["guard"] = (context) => {
  if (context.form.isBillingAndShippingTheSame) {
    return false;
  }
  return (
    guardOnlyWhenNotASavedAddress("billing") as Required<
      GenericTextFieldValidationOptions<ShippingForm, ShippingFormApp>
    >["guard"]
  )(context);
};

export const ShippingFormLogic: GenericFormLogic<
  ShippingForm,
  ShippingFormApp
> = {
  emailAddress: emailAddress_withIsEmailRegisteredAlready_formLogic({
    required: true,
  }),

  shipping_selectedSavedAddressId: savedAddressFormLogic({
    required: true,
    guard: guardSavedAddress_forShipping,
  }),

  shipping_firstName: nameFormLogic({
    required: true,
    guard: guardOnlyWhenNotASavedAddress_forShipping,
  }),
  shipping_lastName: nameFormLogic({
    required: true,
    guard: guardOnlyWhenNotASavedAddress_forShipping,
  }),
  shipping_mobile: mobileFormLogic({
    required: true,
    guard: guardOnlyWhenNotASavedAddress_forShipping,
  }),

  shipping_autocompleteAddress: addressAutocompleteFormLogic({
    required: true,
    guard: guardAutocompleteAddress_forShipping,
  }),

  shipping_manualAddressLine1: addressLineFormLogic({
    required: true,
    guard: guardManualAddress_forShipping,
  }),
  shipping_manualAddressLine2: addressLineFormLogic({
    required: false,
    guard: guardManualAddress_forShipping,
  }),
  shipping_manualAddressSuburb: addressLineFormLogic({
    required: true,
    guard: guardManualAddress_forShipping,
  }),
  shipping_manualAddressPostcode: postcodeFormLogic({
    required: true,
    guard: guardManualAddress_forShipping,
  }),
  shipping_manualAddressState: stateFormLogic({
    required: true,
    guard: guardManualAddress_forShipping,
  }),
  shipping_manualAddressCountry: countryFormLogic({
    required: true,
    guard: guardManualAddress_forShipping,
  }),

  isBillingAndShippingTheSame: isBillingAndShippingTheSameFormLogic({}),

  billing_selectedSavedAddressId: savedAddressFormLogic({
    required: true,
    guard: guardSavedAddress_forBilling,
  }),
  billing_firstName: nameFormLogic({
    required: true,
    guard: guardOnlyWhenNotASavedAddress_forBilling,
  }),
  billing_lastName: nameFormLogic({
    required: true,
    guard: guardOnlyWhenNotASavedAddress_forBilling,
  }),
  billing_mobile: mobileFormLogic({
    required: false,
    guard: guardOnlyWhenNotASavedAddress_forBilling,
  }),

  billing_autocompleteAddress: addressAutocompleteFormLogic({
    required: true,
    guard: guardAutocompleteAddress_forBilling,
  }),

  billing_manualAddressLine1: addressLineFormLogic({
    required: true,
    guard: guardManualAddress_forBilling,
  }),
  billing_manualAddressLine2: addressLineFormLogic({
    required: false,
    guard: guardManualAddress_forBilling,
  }),
  billing_manualAddressSuburb: addressLineFormLogic({
    required: true,
    guard: guardManualAddress_forBilling,
  }),
  billing_manualAddressPostcode: postcodeFormLogic({
    required: true,
    guard: guardManualAddress_forBilling,
  }),
  billing_manualAddressState: stateFormLogic({
    required: true,
    guard: guardManualAddress_forBilling,
  }),
  billing_manualAddressCountry: countryFormLogic({
    required: true,
    guard: guardManualAddress_forBilling,
  }),

  shipping_autofill: shippingAutofillFormLogic(),
  billing_autofill: shippingAutofillFormLogic(),
} as const;
